import UploadFileIcon from "@mui/icons-material/UploadFile";
import {
	Box,
	Button,
	CircularProgress,
	Paper,
	Stack,
	TextField,
	Typography,
} from "@mui/material";
import { JsonViewer } from "Configurations/Components/JsonViewer";
import type { ExtractionResult } from "Infrastructure/Api/Api";
import { AuthenticatedLayout } from "Shared/Layouts/AuthenticatedLayout";
import { useExtractDocumentMutation } from "Test/Api/Mutations/useExtractDocumentMutation";
import { type FC, useState } from "react";

export const TestPage: FC = () => {
	const [selectedFile, setSelectedFile] = useState<File | null>(null);
	const [documentType, setDocumentType] = useState("");
	const [hint, setHint] = useState("");
	const [pageSelection, setPageSelection] = useState("");
	const [result, setResult] = useState<ExtractionResult | null>(null);
	const [error, setError] = useState("");

	const extractMutation = useExtractDocumentMutation();

	const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
		if (event.target.files && event.target.files.length > 0) {
			setSelectedFile(event.target.files[0]);
			setError("");
		}
	};

	const handleExtract = async () => {
		if (!selectedFile) {
			setError("Please select a file");
			return;
		}

		setError("");
		setResult(null);

		try {
			const response = await extractMutation.mutateAsync({
				files: [selectedFile],
				documentType: documentType || undefined,
				hint: hint || undefined,
				pageSelection: pageSelection || undefined,
			});

			if (response.status === 200) {
				// Parse the content field if it's a JSON string
				const data = response.data;
				if (data.content && typeof data.content === "string") {
					try {
						data.content = JSON.parse(data.content);
					} catch {
						// If parsing fails, keep it as string
					}
				}
				setResult(data);
			} else if (response.status === 400) {
				setError("Failed to extract document. Please check your input.");
				return;
			} else if (response.status === 401) {
				setError("Authentication failed. Please sign in or check your API credentials.");
				return;
			} else {
				setError("An unexpected error occurred.");
			}
		} catch (err) {
			setError("Failed to extract document. Please try again.");
		}
	};

	const isLoading = extractMutation.isPending;

	return (
		<AuthenticatedLayout title="Test">
			<Box>
				<Typography variant="h4" mb={3}>
					Test Document Extraction
				</Typography>

				<Stack spacing={3}>
					<Paper sx={{ p: 3 }}>
						<Stack spacing={2}>
							<Box>
								<input
									accept="*/*"
									style={{ display: "none" }}
									id="file-upload"
									type="file"
									onChange={handleFileChange}
								/>
								<label htmlFor="file-upload">
									<Button
										variant="outlined"
										component="span"
										startIcon={<UploadFileIcon />}
										fullWidth
									>
										{selectedFile ? selectedFile.name : "Select File"}
									</Button>
								</label>
							</Box>

							<TextField
								label="Document Type (optional)"
								placeholder="e.g., invoice, contract, receipt"
								value={documentType}
								onChange={(e) => setDocumentType(e.target.value)}
								fullWidth
								helperText="Use 'auto' or leave blank for automatic detection"
							/>

							<TextField
								label="Hint (optional)"
								placeholder="e.g., Focus on line items and total amount"
								value={hint}
								onChange={(e) => setHint(e.target.value)}
								fullWidth
								multiline
								rows={2}
							/>

							<TextField
								label="Page Selection (optional)"
								placeholder="e.g., 1-3, 1,3,5, or 1-3,5,7-9"
								value={pageSelection}
								onChange={(e) => setPageSelection(e.target.value)}
								fullWidth
							/>

							{error && (
								<Typography color="error" variant="body2">
									{error}
								</Typography>
							)}

							<Button
								variant="contained"
								color="primary"
								onClick={handleExtract}
								disabled={isLoading}
								fullWidth
							>
								{isLoading ? (
									<>
										<CircularProgress size={20} sx={{ mr: 1 }} />
										Processing...
									</>
								) : (
									"Extract"
								)}
							</Button>
						</Stack>
					</Paper>

					{result && (
						<Paper sx={{ p: 3 }}>
							<Typography variant="h6" mb={2}>
								Extraction Result
							</Typography>
							<JsonViewer jsonString={JSON.stringify(result, null, 2)} />
						</Paper>
					)}
				</Stack>
			</Box>
		</AuthenticatedLayout>
	);
};
